import VueRouter from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from '../store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import { buildPermissionsFromRouter } from '../helper/permissionsHelper';
import { trackEvent } from '../../shared/amplitude';
import { updateDocumentTitle } from '../../shared/helpers/pageTitle';
import {
  RETURN_TO_PARAM,
  sanitizeReturnTo,
} from '../../shared/helpers/returnTo';
import { REDIS_FEATURE_FLAGS } from '../featureFlags';
import {
  lockedFeatureForPath,
  lockedFeatureURL,
} from '../constants/lockedFeatures';
import { CLAUDIA_ACCESS_PERMISSIONS } from '../constants/permissions';

const routes = [...dashboard.routes];

// One segment per line so adding a passthrough shows up as a single added line
// in review, instead of a rewrite of one ~400-char regex.
const CLAUDIA_ONLY_PASSTHROUGH_SEGMENTS = [
  'audit',
  'claudia',
  'dashboard',
  'conversations',
  'custom_view',
  'inbox',
  'locked',
  'notifications',
  'profile',
  'search',
  'settings/agents',
  // The bill for ClaudIA, on an account that only buys ClaudIA. Listed here
  // AND in CLAUDIA_ONLY_ITEMS (sidebarItems/settings.js): the sidebar deciding
  // to show an entry that this guard bounces is a dead link, and index.spec.js
  // fails when the two lists disagree.
  'settings/billing',
  'settings/general',
  'settings/onboarding',
  'settings/sso-wizard',
  'reports/create-mode',
  'reports/claudia-general-info-omni',
  'reports/claudia-controlled-flows-omni',
  'reports/claudia-quality-omni',
  'reports/claudia-tags-topics-omni',
  'reports/claudia-contents-omni',
  'reports/claudia-agentic-omni',
  'reports/claudia-audit-omni',
  'reports/claudia-other-indicators-omni',
  'reports/claudia-metricas-legado',
];
const CLAUDIA_ONLY_PASSTHROUGH_REGEX = new RegExp(
  String.raw`/accounts/\d+/(${CLAUDIA_ONLY_PASSTHROUGH_SEGMENTS.join(
    '|'
  )})(/|$)`
);
// settings/inboxes is not a blanket passthrough: only the list, the channel
// picker, the ClaudIA wizard, the post-create steps, and an existing inbox's
// settings page — generic channel wizards (whatsapp, email, ...) stay blocked.
const CLAUDIA_ONLY_INBOX_PASSTHROUGH_REGEXES = [
  /\/accounts\/\d+\/settings\/inboxes(\/list|\/new|\/\d+)?\/?$/,
  /\/accounts\/\d+\/settings\/inboxes\/new\/claudia\/?$/,
  /\/accounts\/\d+\/settings\/inboxes\/new\/\d+\/(finish|agents)\/?$/,
];
const CLAUDIA_ONLY_KB_PASSTHROUGH_REGEX =
  /\/accounts\/\d+\/(settings\/labels|label)(\/|$)/;
const claudiaLandingFor = id => `accounts/${id}/dashboard`;

const userCanAccessClaudia = (user, accountId) => {
  const account = (user.accounts || []).find(
    a => Number(a.id) === Number(accountId)
  );
  if (!account) return false;
  const permissions = account.permissions || [];
  return CLAUDIA_ACCESS_PERMISSIONS.some(p => permissions.includes(p));
};

export const router = new VueRouter({ mode: 'history', routes });

router.afterEach((to, from) => {
  trackEvent('page_view', {
    page_name: to.name,
    page_path: to.fullPath,
    from_path: from.fullPath,
  });
});

export const routesWithPermissions = buildPermissionsFromRouter(routes);

export const validateAuthenticateRoutePermission = (to, next, { getters }) => {
  const { isLoggedIn, getCurrentUser: user } = getters;

  if (!isLoggedIn) {
    // Carry the intended destination across the login round trip, so a ticket
    // url opened without a session lands on that ticket instead of the default
    // landing page. A path we can't vouch for degrades to the bare login url.
    const returnTo = sanitizeReturnTo(to.fullPath);
    const loginURL = returnTo
      ? frontendURL('login', { [RETURN_TO_PARAM]: returnTo })
      : '/app/login';
    window.location = loginURL;
    return loginURL;
  }

  const isFeatureEnabled = getters['accounts/isFeatureEnabledonAccount'];
  const accountId = Number(to.params.accountId || user.account_id);
  const claudiaOnly =
    typeof isFeatureEnabled === 'function' &&
    isFeatureEnabled(accountId, REDIS_FEATURE_FLAGS.CLAUDIA_ONLY);

  // Knowledge Bases is enabled for every account (feature flag removed), so
  // the KB-related routes (label settings) are always allowed for claudia_only.
  const isClaudiaOnlyAllowed = path =>
    CLAUDIA_ONLY_PASSTHROUGH_REGEX.test(path) ||
    CLAUDIA_ONLY_INBOX_PASSTHROUGH_REGEXES.some(re => re.test(path)) ||
    CLAUDIA_ONLY_KB_PASSTHROUGH_REGEX.test(path);

  if (claudiaOnly && userCanAccessClaudia(user, accountId)) {
    if (!to.name) {
      return next(frontendURL(claudiaLandingFor(accountId)));
    }
    if (to.name !== 'account_suspended' && !isClaudiaOnlyAllowed(to.path)) {
      // Blocked routes that map to a sellable feature land on their upsell
      // page instead of the silent bounce to the dashboard, so the user sees
      // what the feature does and can ask for it.
      const lockedFeature = lockedFeatureForPath(to.path);
      return next(
        frontendURL(
          lockedFeature
            ? lockedFeatureURL(accountId, lockedFeature)
            : claudiaLandingFor(accountId)
        )
      );
    }
    // account_suspended whitelist: pass through only when the account is
    // genuinely suspended; an active user on /suspended is redirected to
    // /claudia/agents (NOT to /dashboard like the legacy chain would).
    if (to.name === 'account_suspended') {
      const currentAccount = (user.accounts || []).find(
        a => Number(a.id) === accountId
      );
      if (currentAccount && currentAccount.status !== 'active') {
        return next();
      }
      return next(frontendURL(claudiaLandingFor(accountId)));
    }
    // /claudia/* — fall through to validateLoggedInRoutes so membership,
    // permissions, and account status are still enforced.
  }

  if (!to.name) {
    return next(frontendURL(`accounts/${user.account_id}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = i18n => {
  // Tab title follows the route. afterEach (not beforeEach) so a guard that
  // redirects doesn't leave the title pointing at a page we never landed on.
  if (i18n) {
    router.afterEach(to => updateDocumentTitle(to, { i18n, store }));
  }

  // Preload account features (e.g. claudia_only) before the first
  // navigation. setUser only populates currentUser; account features
  // come from accounts/get and are needed by the route guard to decide
  // whether to redirect to /claudia/* on first sign-in. Without this
  // chain, the first navigation races against App.vue#initializeAccount
  // and can land claudia_only users on /dashboard.
  const userAuthentication = store.dispatch('setUser').then(() => {
    if (!store.getters.isLoggedIn) return undefined;
    return store.dispatch('accounts/get').catch(() => {
      // Don't block navigation if accounts fetch fails — features may be
      // stale but rendering anything is better than hanging.
      return undefined;
    });
  });

  router.beforeEach((to, from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    userAuthentication.then(() => {
      return validateAuthenticateRoutePermission(to, next, store);
    });
  });
};

export default router;
